import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import useBlogs from "../../../hooks/useBlogs";
import LiveVisitorCounter from "../../../components/LiveVisitorCounter";
import { useBlogViews } from "../../../hooks/useBlogViews";

// ── Separate card component so hook can be called properly ──
const BlogCard = ({ blog }) => {
  const views = useBlogViews(blog.slug);

  return (
    <Link
      to={`/blog/${blog.slug}`}
      className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col h-full"
    >
      {/* Image */}
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={blog.image}
          alt={blog.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {blog.category && (
          <span className="absolute top-3 left-3 bg-[#355931] text-white text-[14px] font-semibold px-3 py-1 rounded-full shadow-sm">
            {blog.category}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 sm:p-5 md:p-6 flex flex-col flex-grow">
        {/* Meta */}
        <div className="flex items-center gap-3 text-[11px] sm:text-xs text-gray-500 mb-2 flex-wrap">
          <span className="flex items-center gap-1">
            <Calendar size={12} />
            {blog.date}
          </span>
          <LiveVisitorCounter slug={blog.slug} />
          <span className="flex items-center gap-1">
            <Clock size={12} />
            {blog.readTime}
          </span>
          {/* 👁️ Total views */}
          {views !== null && (
            <span className="flex items-center gap-1">
              👁️ {views.toLocaleString()} views
            </span>
          )}
        </div>

        {/* Title */}
        <h2 className="text-base sm:text-lg md:text-xl font-bold text-[#2d3b2f] leading-tight mb-3 line-clamp-2 group-hover:text-[#335235] transition-colors duration-300">
          {blog.title}
        </h2>

        {/* Excerpt */}
        <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-4 line-clamp-3">
          {blog.excerpt}
        </p>

        {/* Read More */}
        <div className="mt-auto pt-2">
          <span className="inline-flex items-center gap-2 text-[#335235] font-semibold text-xs sm:text-sm group-hover:gap-3 transition-all duration-300">
            Read More
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </span>
        </div>
      </div>
    </Link>
  );
};

// ── Main component ──
const SecondBlog = () => {
  const { blogs, loading } = useBlogs();

  if (loading) return (
    <div className="bg-[#eef1ec] min-h-screen flex items-center justify-center">
      <p className="text-[#355931] font-semibold">Loading articles...</p>
    </div>
  );

  return (
    <div className="bg-[#eef1ec] min-h-screen py-8 md:py-12 lg:py-16">
      <div className="container max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {blogs.map((blog) => (
            <BlogCard key={blog.id} blog={blog} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SecondBlog;